import { ConflictHandler } from './types';

/**
 * Central registry for conflict handlers.
 * Allows declarative registration of entity-specific conflict resolution strategies.
 */
export class ConflictHandlerRegistry {
    private handlers: Map<string, ConflictHandler> = new Map();

    /**
     * Register a conflict handler for an entity type.
     * 
     * @param handler - The conflict handler to register
     */
    register<T>(handler: ConflictHandler<T>): void {
        if (this.handlers.has(handler.entityType)) {
            console.warn(
                `[ConflictHandlerRegistry] Overwriting existing handler for entity type: ${handler.entityType}`
            );
        }
        this.handlers.set(handler.entityType, handler);
    }

    /**
     * Get the conflict handler for an entity type.
     * 
     * @param entityType - The entity type to get handler for
     * @returns The conflict handler, or undefined if not registered
     */
    get(entityType: string): ConflictHandler | undefined {
        return this.handlers.get(entityType);
    }

    /**
     * Check if a handler is registered for an entity type.
     * 
     * @param entityType - The entity type to check
     * @returns True if a handler is registered
     */
    has(entityType: string): boolean {
        return this.handlers.has(entityType);
    }

    /**
     * Get all registered entity types.
     * 
     * @returns Array of entity type names
     */
    getRegisteredTypes(): string[] {
        return Array.from(this.handlers.keys());
    }

    /**
     * Clear all registered handlers.
     * Primarily for testing purposes.
     */
    clear(): void {
        this.handlers.clear();
    }
}

/**
 * Global conflict handler registry instance.
 * Import this to register or retrieve conflict handlers.
 */
export const conflictHandlerRegistry = new ConflictHandlerRegistry();
